import { exactHardwarePurchase } from './hardware-purchase';
import { requestJson as request, responseError } from './request-json';
// PERF (render-profile 09.10): постоянные метки пайплайна загрузки сцены —
// сеть+парс бандла измеряются в DevTools без пересборки (см. sceneLoadMarks).
import { sceneMark, sceneMeasure } from '@/features/scene/lib/sceneLoadMarks';
import { exactModuleReadiness } from './module-readiness-contract';
import moduleLibraryContract from '../../../../data/specs/module_library_response.v1.json';
import { exactModuleAtelierPayload } from './module-atelier-contract';
import { isRequestAbort, viewerBundleSliceToBundle, type ViewerBundleSlice } from './viewer-slice';

// Requests can hang forever if the backend stalls (audit: no fetch timeout).
// 30 s is generous for the heaviest bundle/estimate calls yet bounds a dead
// socket. Callers that need longer (SSE, uploads) use their own fetch.
export const CRITICAL_QUERY_TIMEOUT_MS = 12_000;
// A governed kitchen batch validates and materializes every selected catalog
// module before it commits a single bundle revision.  Real U-shaped plans are
// routinely 30-50 modules and can legitimately exceed the generic request
// bound on production hardware.  Keep the command bounded, but do not abort a
// valid mutation while the server is still building it.
export const MODULE_BATCH_TIMEOUT_MS = 180_000;

function exactModuleLibraryPayload(value: ModuleLibraryPayload): ModuleLibraryPayload {
  const finiteCount = (item: unknown) => typeof item === 'number' && Number.isInteger(item) && item >= 0;
  const plainRecord = (item: unknown): item is Record<string, unknown> => !!item
    && typeof item === 'object' && !Array.isArray(item);
  const exactKeys = (item: Record<string, unknown>, allowed: readonly string[]) =>
    Object.keys(item).every((key) => allowed.includes(key));
  const hasKeys = (item: Record<string, unknown>, required: readonly string[]) =>
    required.every((key) => Object.prototype.hasOwnProperty.call(item, key));
  const hasExactKeys = (item: Record<string, unknown>, expected: readonly string[]) =>
    exactKeys(item, expected) && hasKeys(item, expected);
  const sha256 = (item: unknown) => typeof item === 'string' && /^[0-9a-f]{64}$/.test(item);
  const nullableString = (item: unknown) => item === null || typeof item === 'string' && Boolean(item);
  const requiredString = (item: unknown) => typeof item === 'string' && Boolean(item);
  const commonItemKeys = ['id', 'name', 'category', 'sourceKind', 'metaType', 'structuralClass',
    'templateKey', 'tier', 'catalogLayer', 'recipeFamilyKey', 'aliasCount', 'canonicalId',
    'semanticDigest', 'installStatus', 'installBlocker', 'dimensionsMm', 'entitiesCount',
    'panelsCount', 'hardwareCount', 'aiSemantics', 'hasGlb', 'previewSvg'] as const;
  const allowedItemKeys = [...commonItemKeys, 'bucketHint', 'objectCanonStatus', 'objectCanonByRule',
    'commerciallyInstallable', 'manufacturingReadiness', 'manufacturingArtifacts', 'runtimeBuilder',
    'sourceRecipeDigest'] as const;

  /*
   * ПРИЗНАКИ РАСПОЗНАВАНИЯ — ПОЛЕ БЭКЭНДА, А НЕ МУСОР.
   *
   * `holzcom.kitchen-recognition-features.v1` кладёт `kitchen_module_semantics.py`, читает
   * `ai_catalog_match.py`. Здесь его в списке не было, а список — ТОЧНЫЙ, поэтому любой модуль
   * с признаками валил проверку целиком, а вместе с ней и весь ответ каталога: панель писала
   * «Каталог не загрузился. Проверьте backend viewer», хотя бэкэнд отвечал 200 и десятью
   * позициями. Замер 10.09: девять базовых корпусов v4 несут признаки — каталог был пуст весь.
   *
   * Поле проверяется по своей схеме, а не пропускается молча: строгость остаётся, устаревшим
   * был именно список.
   */
  const exactRecognitionFeatures = (item: unknown): boolean => plainRecord(item)
    && item.schema === 'holzcom.kitchen-recognition-features.v1'
    && requiredString(item.status);

  const exactAiSemantics = (item: unknown): boolean => {
    const keys = ['schema', 'semanticId', 'functionalType', 'metaType', 'tier', 'templateKey',
      'structuralClass', 'ownership', 'appliances', 'confidence', 'source', 'searchTerms'] as const;
    const optional = ['recognitionFeatures'] as const;
    if (!plainRecord(item) || !exactKeys(item, [...keys, ...optional]) || !hasKeys(item, keys)) return false;
    if (item.recognitionFeatures !== undefined && !exactRecognitionFeatures(item.recognitionFeatures)) return false;
    const ownership = item.ownership;
    const confidence = item.confidence;
    const source = item.source;
    return item.schema === 'holzcom.ai-kitchen-module-semantics.v1'
      && ['semanticId', 'functionalType', 'metaType', 'tier', 'templateKey', 'structuralClass']
        .every((key) => requiredString(item[key]))
      && plainRecord(ownership) && hasExactKeys(ownership, ['structure', 'fill', 'facade', 'appliance'])
      && Object.values(ownership).every(requiredString)
      && Array.isArray(item.appliances) && item.appliances.every(requiredString)
      && Array.isArray(item.searchTerms) && item.searchTerms.every(requiredString)
      && plainRecord(confidence) && hasExactKeys(confidence, ['value', 'source'])
      && typeof confidence.value === 'number' && Number.isFinite(confidence.value)
      && confidence.value >= 0 && confidence.value <= 1 && requiredString(confidence.source)
      && plainRecord(source)
      && hasExactKeys(source, ['schema', 'sourcePath', 'sourceFileSha256', 'sourcePayloadSha256'])
      && source.schema === 'holzcom.catalog-source-lineage.v1' && requiredString(source.sourcePath)
      && sha256(source.sourceFileSha256) && sha256(source.sourcePayloadSha256);
  };

  const exactReadiness = (item: unknown): boolean => {
    const keys = ['schema', 'status', 'commerciallyInstallable', 'canonicalUnpricedBomSha256',
      'canonicalCutSha256', 'manufacturingLineageSha256', 'productionBomPriceStatus', 'blockers'] as const;
    if (!plainRecord(item) || !hasExactKeys(item, keys)) return false;
    /*
     * ДАЙДЖЕСТ ОТСУТСТВУЮЩЕГО АРТЕФАКТА — NULL, А НЕ ВЫДУМАННАЯ СУММА.
     *
     * Изделие без производственных артефактов (`CATALOG_MANUFACTURING_ARTIFACTS_MISSING`)
     * не имеет ни BOM, ни раскроя, ни родословной — считать их нечего.  Требование
     * sha256 на всех трёх полях означало, что такая позиция вообще не может быть
     * описана честно, и весь ответ каталога объявлялся невалидным: панель писала
     * «Каталог недоступен» вместе с полем поиска.  Замер 12.09: 62 позиции из 1435.
     * Строгость остаётся там, где она что-то значит: у готового к выпуску изделия
     * все три дайджеста обязаны быть настоящими.
     */
    const digestOrNull = (value: unknown) => value === null || sha256(value);
    const ready = item.status === 'ready';
    return item.schema === 'holzcom.manufacturing-readiness.v1'
      && (ready || item.status === 'blocked')
      && typeof item.commerciallyInstallable === 'boolean'
      && (ready
        ? sha256(item.canonicalUnpricedBomSha256) && sha256(item.canonicalCutSha256)
          && sha256(item.manufacturingLineageSha256)
        : digestOrNull(item.canonicalUnpricedBomSha256) && digestOrNull(item.canonicalCutSha256)
          && digestOrNull(item.manufacturingLineageSha256))
      && requiredString(item.productionBomPriceStatus)
      && Array.isArray(item.blockers) && item.blockers.every((blocker) => plainRecord(blocker)
        && exactKeys(blocker, ['code', 'rowCount']) && requiredString(blocker.code)
        && (blocker.rowCount === undefined || finiteCount(blocker.rowCount)));
  };

  const exactItem = (raw: unknown, categories: string[]): boolean => {
    if (!plainRecord(raw) || !exactKeys(raw, allowedItemKeys) || !hasKeys(raw, commonItemKeys)) return false;
    const dimensions = raw.dimensionsMm;
    const status = raw.installStatus;
    if (!requiredString(raw.id) || !requiredString(raw.name) || !requiredString(raw.category)
      || !categories.includes(String(raw.category)) || !requiredString(raw.sourceKind)
      || !requiredString(raw.metaType) || !requiredString(raw.structuralClass)
      || !requiredString(raw.templateKey) || !requiredString(raw.tier) || !requiredString(raw.catalogLayer)
      || !nullableString(raw.recipeFamilyKey) || !nullableString(raw.canonicalId)
      || !sha256(raw.semanticDigest) || !['READY', 'BUILDABLE', 'BLOCKED'].includes(String(status))
      || (status === 'BLOCKED' ? !requiredString(raw.installBlocker) : raw.installBlocker !== null)
      || !finiteCount(raw.aliasCount) || typeof raw.hasGlb !== 'boolean' || !exactAiSemantics(raw.aiSemantics)
      || !plainRecord(dimensions) || !hasExactKeys(dimensions, ['width', 'height', 'depth'])
      || !Object.values(dimensions).every((dimension) => typeof dimension === 'number'
        && Number.isFinite(dimension) && dimension > 0)
      || !finiteCount(raw.entitiesCount) || !finiteCount(raw.panelsCount) || !finiteCount(raw.hardwareCount)
      || !requiredString(raw.previewSvg) || !String(raw.previewSvg).startsWith('<svg')
      || !(raw.bucketHint === undefined || requiredString(raw.bucketHint))) return false;

    if (raw.catalogLayer === 'source_recipe') {
      const aiSemantics = raw.aiSemantics;
      return hasKeys(raw, ['runtimeBuilder', 'sourceRecipeDigest'])
        && raw.objectCanonStatus === undefined && raw.objectCanonByRule === undefined
        && raw.commerciallyInstallable === undefined && raw.manufacturingReadiness === undefined
        && raw.manufacturingArtifacts === undefined && nullableString(raw.runtimeBuilder)
        && sha256(raw.sourceRecipeDigest)
        && plainRecord(aiSemantics) && plainRecord(aiSemantics.source)
        && raw.sourceRecipeDigest === aiSemantics.source.sourcePayloadSha256;
    }
    const artifacts = raw.manufacturingArtifacts;
    return hasKeys(raw, ['objectCanonStatus', 'objectCanonByRule', 'commerciallyInstallable',
      'manufacturingReadiness', 'manufacturingArtifacts'])
      && raw.runtimeBuilder === undefined && raw.sourceRecipeDigest === undefined
      && nullableString(raw.objectCanonStatus) && plainRecord(raw.objectCanonByRule)
      && Object.values(raw.objectCanonByRule).every(finiteCount)
      && typeof raw.commerciallyInstallable === 'boolean' && exactReadiness(raw.manufacturingReadiness)
      && plainRecord(artifacts)
      && hasExactKeys(artifacts, ['canonicalUnpricedBom', 'productionBom', 'cutlist', 'lineage'])
      && Object.values(artifacts).every((flag) => typeof flag === 'boolean');
  };

  const topKeys = moduleLibraryContract.topLevelKeys;
  const errorKeys = moduleLibraryContract.errorKeys;
  const statusCountKeys = moduleLibraryContract.statusCountKeys;
  if (!plainRecord(value) || !hasExactKeys(value, topKeys)
    || value.schema !== moduleLibraryContract.schema
    || !requiredString(value.generatedAt) || !Number.isFinite(Date.parse(value.generatedAt))
    || (value.previewMode !== 'material' && value.previewMode !== 'line') || !finiteCount(value.count)
    || !Array.isArray(value.categories) || !value.categories.every(requiredString)
    || new Set(value.categories).size !== value.categories.length
    || (value.count === 0 ? value.categories.length !== 0 : value.categories.length === 0)
    || !finiteCount(value.offset) || !finiteCount(value.limit) || value.limit === 0
    || typeof value.hasMore !== 'boolean' || !Array.isArray(value.errors)
    || !value.errors.every((error) => plainRecord(error) && hasExactKeys(error, errorKeys)
      && requiredString(error.id) && requiredString(error.detail))
    || !plainRecord(value.statusCounts)
    || !hasExactKeys(value.statusCounts, statusCountKeys)
    || !Object.values(value.statusCounts).every(finiteCount)
    || Object.values(value.statusCounts).reduce((sum, count) => sum + Number(count), 0) !== value.count
    || !Array.isArray(value.items) || value.items.length > value.count || value.items.length > value.limit
    || value.hasMore !== (value.offset + value.items.length < value.count)
    || value.items.some((item) => !exactItem(item, value.categories))
  ) throw new Error('MODULE_CATALOG_RESPONSE_INVALID');
  // Packaged-index failures must not hide the code-owned baseline catalogue.
  // They remain explicit in `errors`; the query is fatal only when no usable
  // item survived the failing external contour.
  if (value.errors.length > 0 && value.items.length === 0) throw new Error(value.errors[0].id);
  return value;
}

/**
 * `generatedAt` — не всегда отметка времени.
 *
 * Каталожные бандлы несут ИМЕНОВАННОЕ происхождение `catalog-source`
 * (`backend/catalog_module_builder.py: CATALOG_GRAPH_GENERATED_AT`): такой
 * бандл пришёл из каталожного источника, а не из прогона генерации в
 * какой-то момент времени. Клиент же требовал разбираемую дату — и
 * `Date.parse('catalog-source')` даёт `NaN`, после чего ВЕСЬ бандл
 * отвергался как `MODULE_BUNDLE_RESPONSE_INVALID`.
 *
 * Замер по полю (а не по вхождению подстроки в файл): так помечены **1359**
 * бандлов из 1434 — вьюер не открывал ни один из них. Ещё 74 этого поля не
 * несут вовсе и отвергаются по другой причине, одному проставлена дата.
 *
 * Прежде здесь стояло 1372 — число из ЧУЖОГО множества: столько файлов
 * содержат строку где угодно, в том числе в `catalogPublicationLineage`.
 * Совпадение с замером соседнего коммита и сбило меня.
 *
 * Допускается ровно одно именованное значение, а не «что угодно строкой»:
 * иначе проверка перестаёт что-либо значить.
 */
const CATALOG_SOURCE_PROVENANCE = 'catalog-source';

function generatedAtIsDeclared(value: unknown): boolean {
  if (typeof value !== 'string' || !value) return false;
  if (value === CATALOG_SOURCE_PROVENANCE) return true;
  return Number.isFinite(Date.parse(value));
}

export function exactUniversalBundle(value: UniversalBundle): UniversalBundle {
  const plainRecord = (item: unknown): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item);
  const finiteVec = (item: unknown) => Array.isArray(item) && item.length === 3
    && item.every((part) => typeof part === 'number' && Number.isFinite(part));
  const recordOrUndefined = (item: unknown) => item === undefined
    || plainRecord(item);
  const canonicalRecords = ['buildSpec', 'canonicalCut', 'canonicalFurnitureGraph', 'canonicalUnpricedBom',
    'catalogEntityBinding', 'catalogResolution', 'erpSmetaImportProjection', 'publicationLineage',
    'commercialFacadeCalculation', 'reviewedLayoutOverrideResult', 'viewerHints'] as const;
  const scene = value?.canonicalScene;
  const legacyBundleTransport = plainRecord(value)
    && value.schema === undefined
    && typeof value.sourceKind === 'string' && value.sourceKind.length > 0
    && typeof value.modelRowsSource === 'string' && value.modelRowsSource.length > 0
    && Array.isArray(value.modelRows)
    && plainRecord(scene);
  if (!plainRecord(value)
    /* ПРИЗНАК ВЫЧИСЛЯЛСЯ, НО НЕ ИСПОЛЬЗОВАЛСЯ.
       `legacyBundleTransport` выше распознаёт старый транспорт библиотеки
       модулей — без верхней `schema`, но со своим `sourceKind`,
       `modelRowsSource`, `modelRows` и сценой. Ниже он применяется (строка
       с `transportSchema`), а заслон требовал верхнюю схему БЕЗУСЛОВНО: до
       ветки легаси дело не доходило никогда, и такой бандл отвергался
       `MODULE_BUNDLE_RESPONSE_INVALID`. Отказ на ПУСТОЙ сцене при этом
       остаётся: он ниже, отдельной проверкой `entities.length === 0`. */
    || (value.schema !== 'holzcom.universal_bundle.v1' && !legacyBundleTransport)
    || typeof value.sourceKind !== 'string' || !value.sourceKind
    || (value.generatedAt !== undefined && !generatedAtIsDeclared(value.generatedAt))
    || (value.bundleVersion !== undefined && (!Number.isInteger(value.bundleVersion) || value.bundleVersion < 0))
    || (value.bundleHash !== undefined && !/^[0-9a-f]{16}$/.test(value.bundleHash))
    || (value.modelName !== undefined && (typeof value.modelName !== 'string' || !value.modelName))
    || (value.modelRowsSource !== undefined && (typeof value.modelRowsSource !== 'string' || !value.modelRowsSource))
    || canonicalRecords.some((key) => !recordOrUndefined(value[key]))
    || !plainRecord(scene)
    || ![undefined, 'holzcom.canonical_scene.v1', 'holzcom.webgl.universal_scene.v2'].includes(scene.schema as string | undefined)
    || ![undefined, 'mm', 'мм'].includes(scene.units as string | undefined)
    || !Array.isArray(scene.entities) || scene.entities.length === 0
    || scene.entities.some((entity) => !plainRecord(entity)
      || typeof entity.id !== 'string' || !entity.id || typeof entity.kind !== 'string' || !entity.kind
      || (entity.domain !== undefined && !plainRecord(entity.domain))
      || !recordOrUndefined(entity.semantic)
      || (entity.transform !== undefined && (!plainRecord(entity.transform)
        || (entity.transform.originMm !== undefined && !finiteVec(entity.transform.originMm))
        || (entity.transform.pivotMm !== undefined && !finiteVec(entity.transform.pivotMm))
        || (entity.transform.rotationYDeg !== undefined
          && (typeof entity.transform.rotationYDeg !== 'number' || !Number.isFinite(entity.transform.rotationYDeg)))))
      || (entity.render !== undefined && (!plainRecord(entity.render)
        || (entity.render.center != null && !finiteVec(entity.render.center))
        || (entity.render.size != null && !finiteVec(entity.render.size)))))
  ) throw new Error('MODULE_BUNDLE_RESPONSE_INVALID');
  // The renderer consumes one canonical shape.  Importers may however emit a
  // supported older wire schema, Cyrillic millimetres, or fields added after
  // this frontend was built.  Normalize only those named transport variants;
  // preserve all fields and retain the original wire identity as provenance.
  const transportSchema = typeof scene.schema === 'string' ? scene.schema : null;
  const transportUnits = typeof scene.units === 'string' ? scene.units : null;
  const canonicalScene = {
    ...scene,
    schema: 'holzcom.canonical_scene.v1',
    units: 'mm',
    ...(transportSchema === 'holzcom.canonical_scene.v1' ? {} : { transportSchema }),
    ...(transportUnits === 'mm' ? {} : { transportUnits }),
  };
  return {
    ...value,
    schema: 'holzcom.universal_bundle.v1',
    ...(legacyBundleTransport ? { transportSchema: 'legacy.universal_bundle.no_top_schema.v1' } : {}),
    canonicalScene,
  } as UniversalBundle;
}

import type {
  HardwareCatalogItem,
  HardwareMaterialSelection,
  AutoDimensionsPayload,
  CatalogMaterialSwapCommand,
  CatalogMaterialSwapResult,
  BazisExportResult,
  BazisOutputMode,
  ConstraintWhatIfPayload,
  CutlistPayload,
  CutlistNestingPayload,
  OffcutLedgerPayload,
  DimensionChainPayload,
  FacadeLeafWidthsPreviewPayload,
  FacadeLeafWidthsPreviewResult,
  FacadeSchemesPayload,
  HardwareGraphPayload,
  GeometryDiffPayload,
  RevisionComparison,
  Graph1Session,
  IngestResponse,
  LiveEstimate,
  MaterialLibraryPayload,
  ModelAnalysis,
  ModelHistory,
  ModuleFillOperationPayload,
  ModuleInstanceLifecycleResult,
  ModuleInstanceResult,
  ModuleFillState,
  ModuleAtelierApplyResult,
  ModuleAtelierIntent,
  ModuleAtelierPayload,
  ModuleAtelierResolution,
  ModuleLibraryPayload,
  ModulePreviewGeometryPayload,
  ModulePlacementApplyResult,
  ModulePlacementPreviewResult,
  ModulePositionExchangePreviewResult,
  ModulePositionExchangeApplyResult,
  ModuleOperationsPayload,
  ReplaceConnectorPlanePayload,
  ReplaceConnectorPlaneResult,
  ScenePipelineReport,
  ModuleStatePayload,
  ModelSummary,
  ModelsPipelineSummary,
  ProductionReadinessPayload,
  ModuleReadinessPayload,
  UnifiedValidationPayload,
  UniversalBundle,
  VariationMatrixPayload,
  WallFreeSpansPayload,
} from './types';

export type CarcassDetailOverrides = Record<string, { thicknessMm?: number; panelThicknessMm?: number; variant?: string; role?: string }>;

export type RebuildModuleBody = {
  carcassType?: string;
  facadeSpec?: string;
  kind?: string;
  w?: number;
  h?: number;
  d?: number;
  base?: string;
  baseHeight?: number;
  plinthSetback?: number;
  podiumSetback?: number;
  bottomOverlayOutset?: number;
  topOverlayOutset?: number;
  openMode?: string;
  facadeProfile?: string;
  facadeMaterial?: string;
  // Задняя стенка и толщина плиты — оси корпуса из `holzcom.catalog_recipe.v2/v3`
  // (`carcass.backMode`, `carcass.panelThicknessMm`). Маршрут пересборки принимает оба ключа
  // (см. `REBUILD_MODULE_BODY_KEYS` в `backend/module_edit_commands.py`); раньше здесь их не
  // было, из-за чего ателье корпуса не могло их отправить, хотя владелец операции
  // (`ModuleAggregate.rebuild_parametric`) их принимал.
  backMode?: string;
  panelThicknessMm?: number;
  detailOverrides?: CarcassDetailOverrides;
  // Пенальные ярусы (`holzcom.catalog_recipe.v3`, `recipe.verticalZones`). Владелец
  // (`ModuleAggregate.rebuild_parametric` → `apply_vertical_zones`) принимает их только у
  // `kind='tall'`. Форма ПОВТОРЯЕТ `VerticalZone` из `carcass-atelier/carcass-recipe.ts` —
  // владелец типа тот файл (это зеркало контракта), а не этот: `shared/api` не тянет
  // импорт из `features/*`, чтобы не завести обратную зависимость слоя. Совпадение формы —
  // структурное (TS), а расхождение полей поймает `toApiRebuildBody`, которая их и сводит.
  verticalZones?: Array<{
    id: string; heightMm: number | null; ratio: number | null;
    kind: 'storage' | 'open' | 'drawers' | 'appliance';
    facade: {
      columns: number; rows: number; materialClass: 'paint' | 'glass';
      facadeProfile: string; mount: 'overlay' | 'inset'; openMode: string;
    } | null;
    shelves: { count: number; shelfType: string; materialClass: 'corpus' };
    drawers: number;
    appliance: 'oven' | 'microwave' | 'dishwasher' | 'washer' | 'fridge' | null;
  }>;
  expectedBundleVersion: number;
};

type IngestPayload = {
  source: File;
  name?: string;
  smeta?: File | null;
  viewerJson?: File | null;
  bazisOutput?: BazisOutputMode;
};

async function ingestRequest<T = IngestResponse>(url: string, form: FormData): Promise<T> {
  // Uploads/PDF ingest can legitimately take minutes — no client timeout here.
  const response = await fetch(url, { method: 'POST', body: form });
  if (!response.ok) {
    throw await responseError(url, response);
  }
  return response.json() as Promise<T>;
}

/**
 * Evidence-frame batches are locally rendered PNGs.  A complete seven-view
 * kitchen set can legitimately take longer than the ordinary API watchdog to
 * JSON-encode, upload and hash on a small production instance.  This is an
 * upload, like source ingest, rather than an interactive JSON mutation: keep
 * it cancellable only by navigation and let the server return its own error.
 */
async function evidenceFramesRequest<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await responseError(url, response);
  }
  return response.json() as Promise<T>;
}

export type FillCountRangesPayload = Readonly<{
  schema: 'fill.count_ranges.v1';
  ranges: Readonly<Record<string, Readonly<{ min: number; max: number; default: number }>>>;
}>;

/** N2: ответ read-only превью цены плана наполнения (fill/preview-price).
 *  Ряд с отозванной ставкой платит 0 и обязан нести commercialStatus —
 *  тот же закон, что у BOM-строк фурнитуры. */
export type FillPreviewPricePayload = Readonly<{
  ok: boolean;
  schema: 'holzcom.fill.preview_price.v1';
  totalRub: number;
  rows: readonly Readonly<{
    operation: string;
    hardwareRole: string;
    label: string;
    count: number;
    unitRub: number;
    sumRub: number;
    commercialStatus?: string;
  }>[];
  panelOperations: number;
  notes: readonly string[];
  bundleVersion?: number;
}>;

/** N2: тело запроса превью цены — подмножество полей fill-операции,
 *  влияющих на цену (белый список бэкенда: extra=forbid). */
export type FillPreviewPriceOperation = Readonly<{
  operation: string;
  slotId?: string;
  count?: number;
  shelfType?: string;
  withPins?: boolean;
  heightRatio?: number;
  mode?: string;
  value?: number;
}>;

export const api = {
  // A3: карантин — состояние пайплайна, лобби должно его ВИДЕТЬ (как и в
  // modelsPaged ниже); открытие карантинной модели сервер по-прежнему блокирует.
  models: (opts: { includeQuarantined?: boolean } = {}) =>
    request<{ models: ModelSummary[] }>(`/api/models${opts.includeQuarantined ? '?includeQuarantined=1' : ''}`)
      .then((r) => r.models),
  /* Аудит прода 10.10: единственный серверный источник чисел чипов пайплайна.
     Один запрос, группировка на бэкенде; кэша нет — числа честные на момент
     запроса. includeQuarantined=1 — та же популяция, что у строк лобби. */
  modelsPipelineSummary: (opts: { includeQuarantined?: boolean } = {}, signal?: AbortSignal) =>
    request<ModelsPipelineSummary>(
      `/api/models/pipeline/summary${opts.includeQuarantined ? '?includeQuarantined=1' : ''}`,
      { signal },
      CRITICAL_QUERY_TIMEOUT_MS,
    ),
  /* VIS-18: multi-room мастер. `rooms` (1..10) — единственный контракт
     бэкенда (apartment_create.RoomCreateSpec). */
  createApartment: (body: {
    name: string; ceilingHeightMm: number;
    rooms: Array<{
      name: string; widthMm: number; depthMm: number;
      offsetXMm?: number; offsetZMm?: number;
      floorMaterialId?: string; wallMaterialId?: string; ceilingMaterialId?: string;
    }>;
  }) =>
    request<{ ok: true; modelId: string; bundleVersion: number; model: ModelSummary }>('/api/models/apartment', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }),
  // (D12 fix) Paginated variant — frontend opts in by passing pageSize.
  modelsPaged: (opts: { page?: number; pageSize?: number; q?: string; includeQuarantined?: boolean }, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (opts.page) params.set('page', String(opts.page));
    if (opts.pageSize) params.set('pageSize', String(opts.pageSize));
    if (opts.q) params.set('q', opts.q);
    // A3-лобби: карантин — состояние пайплайна, оператор должен его ВИДЕТЬ.
    // Открытие карантинной модели сервер по-прежнему блокирует (423).
    if (opts.includeQuarantined) params.set('includeQuarantined', '1');
    const qs = params.toString();
    return request<{
      models: ModelSummary[];
      page?: number;
      pageSize?: number;
      total?: number;
      totalPages?: number;
    }>(`/api/models${qs ? `?${qs}` : ''}`, { signal }, CRITICAL_QUERY_TIMEOUT_MS);
  },
  /**
   * ВЬЮЕР-СЛАЙС ПЕРВЫМ, ПОЛНЫЙ БАНДЛ — ФОЛБЭК.
   *
   * Слайс (`/viewer-bundle`) несёт те же canonicalScene-сущности для сцены,
   * но без производственных секций: на showcase-apartment это ~4 МБ вместо
   * ~37 МБ компактного JSON. Любой отказ слайса — 404 (старый бэкенд),
   * битая схема, сетевой сбой — откатывает на прежний полный путь, поэтому
   * старые модели продолжают открываться. Прерванный запрос фолбэку не
   * подлежит: отмена есть отмена.
   */
  bundle: async (id: string, signal?: AbortSignal) => {
    try {
      const slice = await request<ViewerBundleSlice>(
        `/api/models/${encodeURIComponent(id)}/viewer-bundle`, { signal }, CRITICAL_QUERY_TIMEOUT_MS,
      );
      return exactUniversalBundle(viewerBundleSliceToBundle(slice));
    } catch (error) {
      if (isRequestAbort(error)) throw error;
      sceneMark('scene:bundle-fetch-start');
      return request<UniversalBundle>(
        `/api/models/${encodeURIComponent(id)}/bundle`, { signal }, CRITICAL_QUERY_TIMEOUT_MS,
      ).then(exactUniversalBundle).finally(() => {
        sceneMark('scene:bundle-fetch-end');
        sceneMeasure('scene:bundle-fetch', 'scene:bundle-fetch-start', 'scene:bundle-fetch-end');
      });
    }
  },
  createModelShare: (id: string, body: { expiresInDays?: number; label?: string } = {}) =>
    request<{ ok: boolean; modelId: string; token: string; expiresAt: string; scope: string }>(
      `/api/models/${encodeURIComponent(id)}/share-links`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      },
    ),
  scenePipelineReport: (id: string) =>
    request<ScenePipelineReport>(`/api/scene/pipeline/reports/${encodeURIComponent(id)}`),
  autoDimensions: (id: string) =>
    request<AutoDimensionsPayload>(`/api/models/${encodeURIComponent(id)}/auto-dimensions`),
  structure: (id: string) => request<UniversalBundle['modelGraph']>(`/api/models/${encodeURIComponent(id)}/structure`),
  analysis: (id: string) => request<ModelAnalysis>(`/api/models/${encodeURIComponent(id)}/analysis`),
  sourceVerification: (id: string) => request<{
    status: 'PASS' | 'FAIL'; productionGate: { pass: boolean; errors: number };
    runId?: string; createdAt?: number;
    summary: { requirements?: number; matched?: number; requiredViews?: number; verifiedViews?: number; sourceImages?: number; renders?: number; visionViews?: number; observations?: number; actions?: number; semanticCategories?: number; issues: number };
    issues: Array<{ code: string; severity: string; message: string; expected?: unknown; actual?: unknown }>;
    visionRuns?: Array<{ view: string; observations?: Array<{ id: string; category: string; description: string; status: string; requiresAction: boolean }>; actions?: Array<{ id: string; type: string; targetId?: string | null; reasonObservationIds: string[]; params: Record<string, unknown> }> }>;
    actionPlans?: Array<{ valid: boolean; calls: Array<{ actionId: string; method: string; path: string; body: Record<string, unknown>; targetRef?: string; producesRef?: string }>; errors: Array<{ actionId?: string; message: string }> }>;
  }>(`/api/models/${encodeURIComponent(id)}/source-verification`),
  sourceVerificationReadiness: (id: string) => request<{
    available: boolean; provider: string; model?: string; code?: string | null; message: string;
  }>(`/api/models/${encodeURIComponent(id)}/source-verification/readiness`),
  runSourceVerification: (id: string) => request<{ jobId: string; state: string; statusUrl: string }>(
    `/api/models/${encodeURIComponent(id)}/source-verification/run`, { method: 'POST' },
  ),
  uploadSourceVerificationSceneFrames: (id: string, frames: Array<{ id: string; dataUrl: string; cameraPose?: Record<string, unknown>; captureEngine?: string }>) => evidenceFramesRequest<{
    ok: boolean; canonicalGraphSha256: string; frames: Array<{ id: string; path: string; sha256: string; perceptualHash?: string }>;
  }>(`/api/models/${encodeURIComponent(id)}/source-verification/scene-frames`, { frames }),
  sourceVerificationJob: (id: string, jobId: string) => request<{
    id: string; state: 'queued' | 'running' | 'done' | 'error' | 'cancelled' | 'interrupted'; error?: string | null;
    startedAt?: number | null;
    progress?: { stage: string; completed: number; total: number; currentView?: string; message?: string };
  }>(`/api/models/${encodeURIComponent(id)}/source-verification/jobs/${encodeURIComponent(jobId)}`),
  compileSourceVerificationActions: (id: string, actions: Array<Record<string, unknown>>) => request<{
    valid: boolean; calls: Array<{ actionId: string; method: string; path: string; body: Record<string, unknown>; targetRef?: string; producesRef?: string }>;
  }>(`/api/models/${encodeURIComponent(id)}/source-verification/actions/compile`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actions }),
  }),
  executeFurnitureUserAction: (call: { method: string; path: string; body: Record<string, unknown> }) => {
    const allowed = [
      /^\/api\/models\/[^/]+\/source-graph\/actions$/,
      /^\/api\/models\/[^/]+\/cabinet-instances$/,
      /^\/api\/models\/[^/]+\/modules\/instances\/[^/]+\/remove$/,
      /^\/api\/models\/[^/]+\/entities\/[^/]+\/move$/,
      /^\/api\/models\/[^/]+\/modules\/[^/]+\/resize$/,
      /^\/api\/models\/[^/]+\/modules\/[^/]+\/fill$/,
      /^\/api\/models\/[^/]+\/materials\/swap-(facade|corpus)$/,
    ];
    if (!allowed.some((pattern) => pattern.test(call.path)) || !['POST', 'PATCH'].includes(call.method)) {
      return Promise.reject(new Error('Недопустимая команда конструктора'));
    }
    return request<Record<string, unknown>>(call.path, {
      method: call.method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(call.body),
    });
  },
  validation: (id: string, signal?: AbortSignal) => request<UnifiedValidationPayload>(
    `/api/models/${encodeURIComponent(id)}/validation`, { signal }, CRITICAL_QUERY_TIMEOUT_MS,
  ),
  hardwarePurchase: (id: string, signal?: AbortSignal) => request<unknown>(
    `/api/models/${encodeURIComponent(id)}/hardware-purchase`, { signal, cache: 'no-store' }, CRITICAL_QUERY_TIMEOUT_MS,
  ).then(value => exactHardwarePurchase(value, id)),
  productionReadiness: (id: string) => request<ProductionReadinessPayload>(`/api/models/${encodeURIComponent(id)}/production-readiness`),
  moduleReadiness: (id: string, moduleId: string, expectedBundleVersion: number, signal?: AbortSignal) => {
    if (!Number.isSafeInteger(expectedBundleVersion) || expectedBundleVersion < 0) {
      return Promise.reject(new Error('MODULE_READINESS_REVISION_INVALID'));
    }
    return request<ModuleReadinessPayload>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/readiness?expectedBundleVersion=${expectedBundleVersion}`,
      { signal }, CRITICAL_QUERY_TIMEOUT_MS,
    ).then(result => exactModuleReadiness(result, id, moduleId, expectedBundleVersion));
  },
  ergonomics: (id: string, statureMm?: number) =>
    request<{
      ok: boolean; issueCount: number; issues: { severity: string; rule: string; message: string; moduleIds: string[] }[];
      reach?: {
        schema: string; statureMm: number; isDefaultStature: boolean;
        zones: { moduleId: string; handleYMm: number; verdict: string; note: string }[];
        warnings: { moduleId: string; handleYMm: number; verdict: string; note: string }[];
      };
    }>(
      `/api/models/${encodeURIComponent(id)}/ergonomics${statureMm != null ? `?statureMm=${encodeURIComponent(statureMm)}` : ''}`),
  constraints: (id: string) =>
    request<{
      constraints: {
        id: string; type: string; a?: string; b?: string; module?: string;
        axis?: string; edge?: string; valueMm?: number; fromMm?: number; toMm?: number;
      }[];
    }>(`/api/models/${encodeURIComponent(id)}/constraints`),
  dimensionChain: (id: string, axis: string, moduleIds: string[] = []) => {
    const params = new URLSearchParams({ axis });
    if (moduleIds.length) params.set('moduleIds', moduleIds.join(','));
    return request<DimensionChainPayload>(
      `/api/models/${encodeURIComponent(id)}/dimension-chain?${params.toString()}`,
    );
  },
  constraintWhatIf: (id: string, changes: Array<Record<string, unknown>>) =>
    request<ConstraintWhatIfPayload>(`/api/models/${encodeURIComponent(id)}/constraints/what-if`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ changes }),
    }),
  geometryDiff: (id: string, againstModelId: string) => {
    const params = new URLSearchParams({ againstModelId });
    return request<GeometryDiffPayload>(
      `/api/models/${encodeURIComponent(id)}/geometry-diff?${params.toString()}`,
    );
  },
  productionSpec: (id: string) =>
    request<{
      panelCount: number;
      nesting: {
        totalSheets: number | null;
        yield?: { utilizationPct?: number; wastePct?: number } | null;
        unplaced?: { id: string; material?: string; wMm?: number; hMm?: number; reason: string }[] | null;
      };
      edgeBandingTotalM: number;
      drillingTotal: number;
      drillingByKind: Record<string, number>;
    }>(`/api/models/${encodeURIComponent(id)}/production-spec`),
  swapCatalogMaterial: (
    id: string,
    kind: 'facade' | 'corpus',
    body: CatalogMaterialSwapCommand & { expectedBundleVersion: number },
  ) =>
    request<CatalogMaterialSwapResult>(`/api/models/${encodeURIComponent(id)}/materials/swap-${kind}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  cutlist: (id: string) => request<CutlistPayload>(`/api/models/${encodeURIComponent(id)}/cutlist`),
  cutlistNesting: (id: string) =>
    request<CutlistNestingPayload>(`/api/models/${encodeURIComponent(id)}/cutlist/nesting`),
  offcutLedger: (id: string) =>
    request<OffcutLedgerPayload>(`/api/models/${encodeURIComponent(id)}/cutlist/offcuts`),
  moduleState: (id: string) => request<ModuleStatePayload>(`/api/models/${encodeURIComponent(id)}/modules/state`),
  moduleFill: (id: string, moduleId: string) =>
    request<ModuleFillState>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/fill`,
    ),
  previewFacadeLeafWidths: (
    id: string,
    moduleId: string,
    payload: FacadeLeafWidthsPreviewPayload,
    signal?: AbortSignal,
  ) =>
    request<FacadeLeafWidthsPreviewResult>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/fill/facades/preview-widths`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation: 'facades', ...payload }),
        signal,
      },
    ),
  moduleAtelier: (id: string, moduleId: string, signal?: AbortSignal) =>
    request<ModuleAtelierPayload>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/atelier`,
      { signal },
    ).then((value) => exactModuleAtelierPayload(value, id, moduleId)),
  applyModuleAtelier: (
    id: string,
    moduleId: string,
    payload: {
      intent: ModuleAtelierIntent;
      resolution: ModuleAtelierResolution;
      createSnapshot: boolean;
      expectedBundleVersion: number;
    },
  ) => request<ModuleAtelierApplyResult>(
    `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/atelier/apply`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  ),
  applyModuleFill: (
    id: string,
    moduleId: string,
    payload: ModuleFillOperationPayload,
  ) =>
    request<ModuleFillState>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/fill`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ),
  moveEntity: (
    id: string,
    entityId: string,
    delta: { dx: number; dy: number; dz: number; expectedBundleVersion: number },
  ) =>
    request<{ ok: boolean; entityId: string; delta: number[]; bundleVersion: number; aabb?: { min: number[]; max: number[] }; moduleValidationPass?: boolean }>(
      `/api/models/${encodeURIComponent(id)}/entities/${encodeURIComponent(entityId)}/move`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(delta) },
    ),
  // (A2) project variable registry
  getProjectParams: (id: string) =>
    request<{ ok: boolean; params: Record<string, { value?: number | string; formula?: string; unit?: string; label?: string; builtin?: boolean }>; values: Record<string, number>; errors: Record<string, string> }>(
      `/api/models/${encodeURIComponent(id)}/project-params`,
    ),
  patchProjectParams: (id: string, params: Record<string, unknown>) =>
    request<{ ok: boolean; params: Record<string, unknown>; values: Record<string, number>; errors: Record<string, string> }>(
      `/api/models/${encodeURIComponent(id)}/project-params`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ params }) },
    ),
  deleteProjectParam: (id: string, name: string) =>
    request<{ ok: boolean; removed: boolean; values: Record<string, number>; errors: Record<string, string> }>(
      `/api/models/${encodeURIComponent(id)}/project-params/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    ),
  fillSchemes: () => request<FacadeSchemesPayload>('/api/fill/schemes'),
  /* A7: ревью-конвейер каталога (read-only; гейт — глобальный админ). */
  libraryReviewQueue: () =>
    request<unknown>('/api/module-library/review-queue'),
  libraryReviewPacket: () =>
    request<unknown>('/api/module-library/review-packet'),
  /* A7-batch: атомарное batch-применение ревью (Idempotency-Key обязателен). */
  libraryReviewBatch: (body: unknown, idempotencyKey: string) =>
    request<{ ok: boolean; rowVersion: number; batchSha256: string; idempotentReplay: boolean }>(
      '/api/module-library/review-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify(body),
      }),
  saveFillPreset: (
    id: string,
    body: { name: string; baseSchemeId: string; params?: Record<string, number> },
  ) =>
    request<{ ok: boolean; presets: Array<{ id: string; name: string; baseSchemeId: string; params: Record<string, number>; createdAt: string }> }>(
      `/api/models/${encodeURIComponent(id)}/fill/presets`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  deleteFillPreset: (id: string, presetId: string) =>
    request<{ ok: boolean; presets: Array<{ id: string; name: string; baseSchemeId: string; params: Record<string, number>; createdAt: string }> }>(
      `/api/models/${encodeURIComponent(id)}/fill/presets/${encodeURIComponent(presetId)}`,
      { method: 'DELETE' },
    ),
  /** Границы количества наполнения; владелец — cabinet_fill_domain.FILL_COUNT_RANGE. */
  fillCountRanges: () => request<FillCountRangesPayload>('/api/fill/count-ranges'),
  /* N2: цена плана наполнения ДО применения — read-only тонкий маршрут
   * (существующие константы; панели/фасады не считаются — notes). */
  previewFillPrice: (
    id: string,
    moduleId: string,
    operations: readonly FillPreviewPriceOperation[],
  ) =>
    request<FillPreviewPricePayload>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/fill/preview-price`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operations }),
      },
    ),
  applyFillScheme: (
    id: string,
    moduleId: string,
    payload: {
      schemeId: string;
      slotId?: string;
      /** Посадки VALID_MOUNTS бэкенда, включая half_inset (ФПВ). */
      mount?: 'inset' | 'overlay' | 'half_overlay' | 'half_inset';
      facadeProfile?: string;
      facadeMaterialId?: number;
      facadeMaterialSourceReleaseSha?: string;
      facadeMaterialRowSha256?: string;
      materialProfile?: string;
      openMode?: 'hinged' | 'push' | 'lift' | 'drop_down';
      hingeType?: string;
      aventosType?: string;
      sprossen?: number;
      slideSystem?: 'tandembox' | 'legrabox' | 'movento' | 'quadro' | 'roller';
      softClose?: boolean;
      servoDrive?: boolean;
      pushToOpen?: boolean;
      railing?: boolean;
      thicknessMm?: number;
      moduleIds?: string[];
      params?: Record<string, number>;
      createSnapshot?: boolean;
      expectedBundleVersion?: number;
    },
  ) =>
    request<ModuleFillState>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/fill/scheme`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ),
  removeModuleFillItem: (
    id: string,
    moduleId: string,
    itemId: string,
    createSnapshot: boolean,
    expectedBundleVersion: number,
  ) =>
    request<ModuleFillState>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/fill/items/${encodeURIComponent(itemId)}/remove`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ createSnapshot, expectedBundleVersion }),
      },
    ),
  saveModuleState: (
    id: string,
    payload: {
      transforms?: Record<string, { position: [number, number, number]; scale: [number, number, number] }>;
      notes?: Record<string, string>;
      createSnapshot?: boolean;
      /** VF-FE-11: the flush CAS baseline; the backend requires a StrictInt. */
      expectedBundleVersion?: number;
    },
  ) =>
    request<ModuleStatePayload>(`/api/models/${encodeURIComponent(id)}/modules/state`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  // `none` is a server-only deploy readback mode gated by includeStatuses and
  // cohort=runtime-packages. Product callers always require a rendered card.
  moduleLibrary: (params?: { q?: string; category?: string; preview?: 'material' | 'line'; offset?: number; limit?: number }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (params?.q) query.set('q', params.q);
    if (params?.category) query.set('category', params.category);
    if (params?.preview) query.set('preview', params.preview);
    if (typeof params?.offset === 'number') query.set('offset', String(params.offset));
    if (typeof params?.limit === 'number') query.set('limit', String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return request<ModuleLibraryPayload>(`/api/module-library${suffix}`, { signal }, CRITICAL_QUERY_TIMEOUT_MS)
      .then(exactModuleLibraryPayload);
  },
  /** Свободные места вдоль стен под предмет заданной глубины и ряд заданной высоты. */
  wallFreeSpans: (
    id: string,
    body: { depthMm: number; heightFromMm: number; heightToMm: number; minSpanMm?: number; wallIds?: string[] },
    signal?: AbortSignal,
  ) => request<WallFreeSpansPayload>(
    `/api/models/${encodeURIComponent(id)}/walls/free-spans`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal },
  ),
  modulePreviewGeometry: (catalogId: string) =>
    request<ModulePreviewGeometryPayload>(
      `/api/module-library/${encodeURIComponent(catalogId)}/preview-geometry`,
    ),
  compareHistory: (id: string, beforeVersionId: string, afterVersionId: string, signal?: AbortSignal) => {
    const params = new URLSearchParams({ beforeVersionId, afterVersionId });
    return request<RevisionComparison>(`/api/models/${encodeURIComponent(id)}/history/compare?${params}`, { signal });
  },
  history: (id: string) => request<ModelHistory>(`/api/models/${encodeURIComponent(id)}/history`),
  moduleOperations: (id: string, moduleId: string, limit = 25) =>
    request<ModuleOperationsPayload>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/operations?limit=${encodeURIComponent(String(limit))}`,
    ),
  replaceConnectorPlane: (
    id: string,
    moduleId: string,
    payload: ReplaceConnectorPlanePayload,
  ) =>
    request<ReplaceConnectorPlaneResult>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/connectors/replace-plane`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ),
  materials: () => request<MaterialLibraryPayload>('/api/material-library'),
  hardwareCatalog: () =>
    request<{
      schema: string;
      defaults: Record<string, unknown>;
      groups: Record<string, HardwareCatalogItem[]>;
    }>('/api/hardware-catalog'),
  cadDragSnap: (payload: {
    candidate: [number, number, number];
    size: [number, number, number];
    snapThreshold?: number;
    clearance?: number;
    tier?: 'base' | 'wall' | 'tall' | 'unknown';
    scene: Array<{ id: string; tier?: string; min: [number, number, number]; max: [number, number, number] }>;
    /** Модель, у которой сервер спрашивает СТЕНЫ. Без неё привязки к стене не будет, и ответ
     *  скажет это в `wall_refusal` — раньше вместо стены молча служила плоскость z = 0. */
    modelId?: string | null;
    /** Стена, которая уже держит модуль: состояние гистерезиса, чтобы в углу не дрожало. */
    wallId?: string | null;
  }) =>
    request<{
      schema: string;
      snapped_position: [number, number, number];
      snap_kind: string;
      blocked: boolean;
      blocked_by: string | null;
      wall_id: string;
      wall_axis: number;
      wall_shift_mm: number;
      wall_refusal: string | null;
      insertion_plan?: {
        schema: string;
        axis: 'x' | 'z' | string;
        status: string;
        requiresLayoutSolver?: boolean;
        suggestedPolicies?: string[];
        deficitMm?: number;
        gapMm?: number;
        moduleSizeMm?: number;
        negativeModuleId?: string;
        positiveModuleId?: string;
      } | null;
    }>('/api/cad/drag/snap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        candidate_position: payload.candidate,
        module_size: payload.size,
        snap_threshold_mm: payload.snapThreshold ?? 12,
        clearance_mm: payload.clearance ?? 0,
        tier: payload.tier ?? 'base',
        ...(payload.modelId ? { model_id: payload.modelId } : {}),
        ...(payload.wallId ? { wall_id: payload.wallId } : {}),
        scene_modules: payload.scene.map((s) => ({
          id: s.id,
          tier: s.tier ?? 'base',
          min: s.min,
          max: s.max,
        })),
      }),
    }),
  cadMatesCheck: (payload: {
    mates: Array<{
      kind: 'face' | 'edge' | 'point';
      tolerance_mm: number;
      partA: { id: string; faceMin: [number, number, number]; faceMax: [number, number, number] };
      partB: { id: string; faceMin: [number, number, number]; faceMax: [number, number, number] };
    }>;
  }) =>
    request<{
      ok: boolean;
      results: Array<{ kind: string; ok: boolean; gap_mm: number; partA: string; partB: string }>;
    }>('/api/cad/mates/check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  kitchenConstruct: (payload: {
    shape: 'LINE' | 'L' | 'U';
    wall_a_mm: number;
    wall_b_mm?: number;
    wall_c_mm?: number;
    ceiling_mm?: number;
    upper_zone?: boolean;
    have_lower?: boolean;
    have_tall?: boolean;
  }) =>
    request<{
      ok: boolean;
      layout: string;
      plan: Array<{
        catalog_id: string;
        position: [number, number, number];
        tier: 'base' | 'wall' | 'tall';
        dims_mm: { w: number; h: number; d: number };
        /** L/U shape: B carries 90, C carries 180 degrees. */
        rotation_y_deg?: number;
        /** 'A' | 'B' | 'CORNER' for L-shape layouts. */
        wall?: string;
      }>;
      issues: Array<{ severity: string; code: string; message: string }>;
      summary: { base_used: number; wall_used: number; tall_used?: number; total_width_mm: number; overflow_mm: number };
      inputs?: Record<string, unknown>;
    }>('/api/kitchen/construct', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  // Parametric cabinet templates (carcass + facade-layout + hardware).
  cabinetTemplates: () =>
    request<{
      schema: string;
      presets: Array<{ id: string; name: string; kind: string; w?: number; h?: number; d?: number; facade_spec?: string; previewSvg?: string }>;
      carcassTypes: Array<{ id: string; name: string }>;
      baseCarcasses?: Array<{
        id: string;
        label: string;
        carcassType: string;
        base: string;
        bottomJoin: string;
        topJoin: string;
        editableParameters?: Record<string, number | null>;
      }>;
      facadeLayouts: Record<string, string> | Array<{ id: string; name: string }>;
      optionSchema?: Array<{ id: string; label: string; type: 'enum' | 'bool'; default?: string | boolean | null; choices?: Array<{ id: string; label: string }> }>;
    }>('/api/cabinet-templates'),
  addCabinetToRoom: (
    id: string,
    roomCode: string,
    body: {
      presetId?: string;
      kind?: string;
      w?: number;
      h?: number;
      d?: number;
      facadeSpec?: string;
      facadeMaterialId?: number;
      facadeMaterialSourceReleaseSha?: string;
      facadeMaterialRowSha256?: string;
      corpusMaterialId?: number;
      carcassType?: string;
      base?: string;
      baseHeight?: number;
      plinthSetback?: number;
      podiumSetback?: number;
      bottomOverlayOutset?: number;
      topOverlayOutset?: number;
      openMode?: string;
      name?: string;
      options?: Record<string, unknown>;
      detailOverrides?: CarcassDetailOverrides;
      commandId?: string;
      idempotencyKey?: string;
      expectedVersion: number;
    },
  ) =>
    request<{ ok: boolean; instanceId: string; entitiesAdded: number; kind: string; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/rooms/${encodeURIComponent(roomCode)}/add-cabinet`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  addModuleToRoom: (
    id: string,
    roomCode: string,
    body: { catalogId?: string; name?: string; compositionId?: string; expectedVersion: number },
  ) =>
    request<{ ok: boolean; instanceId: string; entitiesAdded: number; catalogId: string; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/rooms/${encodeURIComponent(roomCode)}/add-module`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  reorderComposition: (
    id: string,
    compositionId: string,
    body: { moduleIds: string[]; expectedVersion: number },
  ) =>
    request<{ ok: boolean; compositionId: string; memberIds: string[]; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/compositions/${encodeURIComponent(compositionId)}/reorder`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  createComposition: (
    id: string,
    body: { name?: string; roomId?: string; moduleIds: string[]; axisPrimary?: 'x' | 'z'; expectedVersion: number },
  ) =>
    request<{ ok: boolean; modelId: string; compositionId: string; moduleIds: string[]; bundleVersion: number; bundleHash: string }>(
      `/api/models/${encodeURIComponent(id)}/compositions`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  updateComposition: (
    id: string,
    compositionId: string,
    body: { name?: string; axisPrimary?: 'x' | 'z'; expectedVersion: number },
  ) =>
    request<{ ok: boolean; modelId: string; compositionId: string; bundleVersion: number; bundleHash: string }>(
      `/api/models/${encodeURIComponent(id)}/compositions/${encodeURIComponent(compositionId)}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  deleteComposition: (id: string, compositionId: string, expectedVersion: number) =>
    request<{ ok: boolean; modelId: string; deletedId: string; bundleVersion: number; bundleHash: string }>(
      `/api/models/${encodeURIComponent(id)}/compositions/${encodeURIComponent(compositionId)}`,
      { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedVersion }) },
    ),
  resizeComposition: (
    id: string,
    compositionId: string,
    body: { wMm?: number; hMm?: number; dMm?: number; expectedVersion: number },
  ) =>
    request<{ ok: boolean; modelId: string; compositionId: string; applied: number; rigidUntouched: number; fillerScaled: number; bundleVersion: number; bundleHash: string }>(
      `/api/models/${encodeURIComponent(id)}/compositions/${encodeURIComponent(compositionId)}/resize`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  autoGroupCompositions: (id: string, expectedVersion: number) =>
    request<{ ok: boolean; modelId: string; created: unknown[]; bundleVersion: number; bundleHash: string }>(
      `/api/models/${encodeURIComponent(id)}/compositions/auto-group`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedVersion }) },
    ),
  buildCabinetPreview: (
    body: {
      presetId?: string;
      kind?: string;
      w?: number;
      h?: number;
      d?: number;
      carcassType?: string;
      facadeSpec?: string;
      openMode?: string;
      facadeMaterialId?: number;
      facadeMaterialSourceReleaseSha?: string;
      facadeMaterialRowSha256?: string;
      corpusMaterialId?: number;
      base?: string;
      baseHeight?: number;
      plinthSetback?: number;
      podiumSetback?: number;
      bottomOverlayOutset?: number;
      topOverlayOutset?: number;
      options?: Record<string, unknown>;
      detailOverrides?: CarcassDetailOverrides;
    },
  ) =>
    request<{ ok: boolean; previewSvg: string; priceTotal: number | null; optionsApplied?: Record<string, unknown>; moduleCount: number; partCount: number }>(
      '/api/cabinet-templates/build',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, preview: true }) },
    ),
  rebuildModule: (
    id: string,
    instanceId: string,
    body: RebuildModuleBody,
  ) =>
    request<{ ok: boolean; instanceId: string; kind: string; carcassType: string | null; facadeSpec: string; w: number; h: number; d: number; partsAdded: number; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(instanceId)}/rebuild`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  // (#5) one-click mirror a module in place across its own centre.
  mirrorModule: (id: string, instanceId: string, axis: 'x' | 'z', expectedBundleVersion: number) =>
    request<{ ok: boolean; instanceId: string; axis: string; partsMirrored: number; mirrored: boolean; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(instanceId)}/mirror`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ axis, expectedBundleVersion }) },
    ),
  moveModuleToRoom: (id: string, moduleId: string, roomId: string, expectedVersion: number) =>
    request<{ ok: boolean; modelId: string; moduleId: string; roomId: string; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(moduleId)}/move-to-room`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ roomId, expectedVersion }) },
    ),
  moveCompositionToRoom: (id: string, compositionId: string, roomId: string, expectedVersion: number) =>
    request<{ ok: boolean; modelId: string; compositionId: string; roomId: string; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/compositions/${encodeURIComponent(compositionId)}/move-to-room`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ roomId, expectedVersion }) },
    ),
  // Rotate a placed module about the vertical (Y/yaw) axis. Pass an absolute
  // `rotationYDeg` (numeric field) OR a `deltaYDeg` increment (interactive
  // gizmo drag). Backend: ModuleRotateOperation → ModuleAggregate.rotate.
  rotateModule: (
    id: string,
    instanceId: string,
    body: { rotationYDeg?: number; deltaYDeg?: number; pivot?: [number, number, number]; expectedBundleVersion: number },
  ) =>
    request<{ ok: boolean; modelId: string; instanceId?: string; rotationYDeg?: number; entitiesRotated?: number; bundleVersion?: number }>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(instanceId)}/rotate`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  // (#3) parametric constraints — facade reveal gap + handle position rule.
  setModuleConstraints: (
    id: string,
    instanceId: string,
    body: { reveal?: number | string; handle?: { edge?: string; offsetMm?: number | string } | Record<string, never>; expectedBundleVersion: number },
  ) =>
    request<{ ok: boolean; instanceId: string; constraints: Record<string, unknown>; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(instanceId)}/constraints`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  replaceModuleDetailVariant: (
    id: string,
    instanceId: string,
    body: { role: string; variant: string; createSnapshot?: boolean; expectedBundleVersion: number },
  ) =>
    request<{ ok: boolean; instanceId: string; role: string; variant: string; overrides: Record<string, unknown>; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/modules/${encodeURIComponent(instanceId)}/detail-variant`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  // (#1) compose a heterogeneous run that exactly fills a niche.
  fitRunToRoom: (
    id: string,
    roomCode: string,
    body: { segments: Array<{ kind?: string; w?: number; facadeSpec?: string; flex?: boolean; minW?: number; maxW?: number }>; nicheW?: number; h?: number; d?: number; base?: string; name?: string; expectedVersion: number },
  ) =>
    request<{ ok: boolean; roomCode: string; instanceIds: string[]; fit: { cabinets: number; spanMm: number; usableMm: number; exactFit: boolean; segments: Array<Record<string, unknown>> }; bundleVersion: number }>(
      `/api/models/${encodeURIComponent(id)}/rooms/${encodeURIComponent(roomCode)}/fit-run`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    ),
  kitchenGaps: (payload: {
    tier: 'base' | 'wall' | 'tall';
    wall_length_mm: number;
    modules: Array<{ id: string; min: [number, number, number]; max: [number, number, number] }>;
  }) =>
    request<{
      ok: boolean;
      gaps: Array<{ kind: 'SMALL' | 'FILLER' | 'MODULE'; start: number; end: number; size_mm: number }>;
    }>('/api/kitchen/gaps', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  aiParseImage: (image: File, promptHint?: string) => {
    const form = new FormData();
    form.set('image', image);
    if (promptHint?.trim()) form.set('prompt_hint', promptHint.trim());
    return ingestRequest('/api/ai/parse-image', form);
  },
  aiParseImages: (images: File[], promptHint?: string) => {
    const form = new FormData();
    images.forEach((image) => form.append('images', image));
    if (promptHint?.trim()) form.set('prompt_hint', promptHint.trim());
    return ingestRequest('/api/ai/parse-images', form);
  },
  // NOTE: singular /api/ai/ingest-image never existed as a public route
  // (fail-closed ingest contract); removed the dead client method.
  aiIngestImages: (
    images: File[],
    sourceRequirements: Record<string, unknown>,
    opts?: { name?: string; promptHint?: string },
  ) => {
    const form = new FormData();
    images.forEach((image) => form.append('images', image));
    form.set('source_requirements', JSON.stringify(sourceRequirements));
    if (opts?.name?.trim()) form.set('name', opts.name.trim());
    if (opts?.promptHint?.trim()) form.set('prompt_hint', opts.promptHint.trim());
    return ingestRequest('/api/ai/ingest-images', form);
  },
  stageImages: (images: File[], opts?: { name?: string; promptHint?: string }) => {
    const form = new FormData();
    images.forEach((image) => form.append('images', image));
    if (opts?.name?.trim()) form.set('name', opts.name.trim());
    if (opts?.promptHint?.trim()) form.set('prompt_hint', opts.promptHint.trim());
    return ingestRequest<{
      ok: boolean; sessionId: string; state: string; graph1: Record<string, unknown>;
      sourceRequirements: Record<string, unknown>;
      next: { method: string; path: string };
    }>('/api/ai/stage-images', form);
  },
  // Provider-free handoff: an external vision agent can submit its canonical
  // Graph-1 plus an independent source-only oracle through the same staged
  // production pipeline.  It is deliberately not a "scene import" endpoint:
  // the server still owns Graph-1 validation and Graph-2 materialization.
  stageExternalGraph1: (images: File[], payload: {
    recognitionGraph: Record<string, unknown>;
    sourceRequirements: Record<string, unknown>;
    name?: string;
  }) => {
    const form = new FormData();
    images.forEach((image) => form.append('images', image));
    form.set('recognition_graph', JSON.stringify(payload.recognitionGraph));
    form.set('source_requirements', JSON.stringify(payload.sourceRequirements));
    if (payload.name?.trim()) form.set('name', payload.name.trim());
    return ingestRequest<{
      ok: boolean; sessionId: string; state: string; graph1: Record<string, unknown>;
    }>('/api/ai/graph1-sessions', form);
  },
  auditExternalGraph1: (sessionId: string, body: { pass: boolean; findings: Array<Record<string, unknown>> }) =>
    request<{
      ok: boolean; accepted: boolean; sessionId: string; state: string; modelId?: string;
      canonicalGraphSha256?: string;
    }>(`/api/ai/graph1-sessions/${encodeURIComponent(sessionId)}/audit`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }),
  graph1Session: (sessionId: string) => request<Graph1Session>(
    `/api/ai/graph1-sessions/${encodeURIComponent(sessionId)}`,
  ),
  repassGraph1Session: (sessionId: string, body?: {
    recognitionGraph?: Record<string, unknown>;
    promptHint?: string;
  }) => request<Graph1Session>(`/api/ai/graph1-sessions/${encodeURIComponent(sessionId)}/repass`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body ?? {}),
  }),
  submitGraph1Feedback: (sessionId: string, body: {
    code: string;
    targetId: string;
    message: string;
    sourceRegion?: number[];
  }) => request<Graph1Session>(`/api/ai/graph1-sessions/${encodeURIComponent(sessionId)}/feedback`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }),
  auditExternalGraph2: (sessionId: string, body: {
    pass: boolean;
    findings: Array<Record<string, unknown>>;
    frameIds: string[];
    canonicalGraphSha256: string;
  }) => request<{
    ok: boolean; accepted: boolean; sessionId: string; state: string; modelId?: string;
    canonicalGraphSha256?: string; frameIds?: string[];
  }>(`/api/ai/graph1-sessions/${encodeURIComponent(sessionId)}/graph2-audit`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }),
  externalGraphAudit: (modelId: string) => request<{
    sessionId: string; modelId: string; state: string;
  }>(`/api/ai/models/${encodeURIComponent(modelId)}/graph-audit`),
  ingest: (payload: IngestPayload) => {
    const form = new FormData();
    form.set('source', payload.source);
    if (payload.name?.trim()) form.set('name', payload.name.trim());
    if (payload.smeta) form.set('smeta', payload.smeta);
    if (payload.viewerJson) form.set('viewer_json', payload.viewerJson);
    form.set('bazis_output', payload.bazisOutput ?? 'none');
    return ingestRequest('/api/ingest', form);
  },
  importCadReference: (modelId: string, source: File, material = 'Импорт') => {
    const form = new FormData();
    form.set('file', source);
    form.set('material', material);
    return ingestRequest<{
      ok: boolean; modelId: string; importId: string; format: 'dxf'; entitiesAdded: number;
      manufacturing: 'reference_only'; bundleVersion: number; bundleHash: string;
    }>(`/api/models/${encodeURIComponent(modelId)}/cad/import`, form);
  },
  exportBazis: (id: string, output: Exclude<BazisOutputMode, 'none'> = 'both') =>
    request<BazisExportResult>(`/api/models/${encodeURIComponent(id)}/bazis/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ output }),
    }),
  addModuleInstance: (
    id: string,
    payload: {
      catalogId: string;
      name?: string;
      position?: [number, number, number];
      layoutPolicy?: 'push' | 'distribute';
      layoutAxis?: 'x' | 'z';
      layoutDistribution?: Record<string, unknown>;
      layoutHints?: Record<string, unknown>;
      createSnapshot?: boolean;
      expectedBundleVersion?: number;
    },
  ) =>
    request<ModuleInstanceResult>(`/api/models/${encodeURIComponent(id)}/modules/instances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  previewModulePlacement: (
    id: string,
    payload: {
      catalogId: string;
      name?: string;
      position: [number, number, number];
      rotationYDeg?: number;
      freeHand?: boolean;
      wallId?: string;
      cornerId?: string;
      tier?: 'lower' | 'upper' | 'mezzanine' | 'tall' | 'free_floor' | 'unknown';
      nicheId?: string;
      /* Три исхода со своими токенами спрашиваются ОДИН раз — на отпускании. Перетаскивание
         просит план тридцать раз в секунду; платить там за выбор, которого никто не
         показывает, незачем. */
      includeOptions?: boolean;
      expectedBundleVersion: number;
      expectedBundleHash: string;
    },
    signal?: AbortSignal,
  ) => request<ModulePlacementPreviewResult>(
    `/api/models/${encodeURIComponent(id)}/modules/placement/preview`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    },
  ),
  applyModulePlacement: (id: string, token: string) => request<ModulePlacementApplyResult>(
    `/api/models/${encodeURIComponent(id)}/modules/placement/apply`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    },
  ),
  previewModulePositionExchange: (id: string, payload: { sourceModuleId: string; targetModuleId: string; expectedBundleVersion: number; expectedBundleHash: string }) => request<ModulePositionExchangePreviewResult>(
    `/api/models/${encodeURIComponent(id)}/modules/position-exchange/preview`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) },
  ),
  applyModulePositionExchange: (id: string, token: string) => request<ModulePositionExchangeApplyResult>(
    `/api/models/${encodeURIComponent(id)}/modules/position-exchange/apply`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) },
  ),
  addCabinetInstance: (
    id: string,
    payload: {
      presetId?: string;
      kind?: string;
      w?: number;
      h?: number;
      d?: number;
      carcassType?: string;
      facadeSpec?: string;
      base?: string;
      baseHeight?: number;
      plinthSetback?: number;
      podiumSetback?: number;
      bottomOverlayOutset?: number;
      topOverlayOutset?: number;
      detailOverrides?: Record<string, { thicknessMm?: number; panelThicknessMm?: number; variant?: string; role?: string }>;
      openMode?: string;
      name?: string;
      options?: Record<string, unknown>;
      autoDimensionMode?: string;
      position?: [number, number, number];
      expectedVersion?: number;
      createSnapshot?: boolean;
    },
  ) =>
    request<ModuleInstanceResult>(`/api/models/${encodeURIComponent(id)}/cabinet-instances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  addModuleInstancesBatch: (
    id: string,
    payload: {
      items: Array<{
        catalogId: string;
        name?: string;
        position?: [number, number, number];
        rotation_y_deg?: number;
      }>;
      createSnapshot?: boolean;
      snapshotLabel?: string;
      expectedBundleVersion: number;
    },
  ) =>
    request<{
      ok: boolean;
      modelId: string;
      added: Array<{ index: number; catalogId: string; instanceId: string; entitiesAdded: number; rowsAdded: number }>;
      errors: Array<{ index: number; catalogId: string; error: string }>;
      model: ModelSummary;
      updatedAt: string;
      bundleVersion?: number;
      bundleHash?: string;
    }>(`/api/models/${encodeURIComponent(id)}/modules/instances/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }, MODULE_BATCH_TIMEOUT_MS),
  removeModuleInstance: (id: string, instanceId: string, expectedBundleVersion: number, createSnapshot = true) =>
    request<ModuleInstanceLifecycleResult>(
      `/api/models/${encodeURIComponent(id)}/modules/instances/${encodeURIComponent(instanceId)}/remove`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ createSnapshot, expectedBundleVersion }),
      },
    ),
  duplicateModuleInstance: (id: string, instanceId: string, expectedBundleVersion: number, createSnapshot = true) =>
    request<ModuleInstanceLifecycleResult>(
      `/api/models/${encodeURIComponent(id)}/modules/instances/${encodeURIComponent(instanceId)}/duplicate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ createSnapshot, expectedBundleVersion }),
      },
    ),
  combineModulesIntoSections: (
    id: string,
    moduleIds: string[],
    expectedBundleVersion: number,
    name?: string,
    createSnapshot = true,
  ) =>
    request<ModuleCombineResult>(
      `/api/models/${encodeURIComponent(id)}/modules/combine`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ moduleIds, expectedBundleVersion, createSnapshot, ...(name ? { name } : {}) }),
      },
    ),
  splitModuleIntoCorpora: (id: string, instanceId: string, expectedBundleVersion: number, createSnapshot = true) =>
    request<ModuleSplitResult>(
      `/api/models/${encodeURIComponent(id)}/modules/instances/${encodeURIComponent(instanceId)}/split`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ createSnapshot, expectedBundleVersion }),
      },
    ),
  /**
   * Куда и на сколько выбранный модуль ещё может сдвинуться — ЗАМЕР СЕРВЕРА.
   *
   * Читающая ручка: считает тот же владелец, что знает соседей и комнату. Свой расчёт на
   * клиенте был бы второй правдой рядом с `wall_free_spans`.
   */
  admissibleModuleMoves: (id: string, instanceId: string, signal?: AbortSignal) =>
    request<{
      ok: true;
      modelId: string;
      moduleId: string;
      moves: Array<{
        axis: 'x' | 'z';
        sign: -1 | 1;
        freeMm: number | null;
        blockedBy: string | null;
        blockedKind: 'module' | 'room' | 'open';
      }>;
    }>(
      `/api/models/${encodeURIComponent(id)}/modules/instances/${encodeURIComponent(instanceId)}/admissible-moves`,
      { signal },
    ),
  /**
   * Вставить СЕКЦИЮ модулей, сохранив их взаимное расположение — одной командой.
   *
   * Вставка по одному дублю ставит каждую копию рядом со СВОИМ оригиналом, и секция
   * разваливается. Здесь одна команда, одна запись в истории, одна отмена.
   */
  pasteModuleSection: (
    id: string,
    payload: {
      sourceInstanceIds: string[];
      anchorMm?: [number, number, number] | null;
      createSnapshot?: boolean;
      expectedBundleVersion: number;
    },
  ) =>
    request<{
      ok: true;
      modelId: string;
      instanceIds: string[];
      placed: Array<{ sourceInstanceId: string; instanceId: string; position: [number, number, number] }>;
      anchorMm: [number, number, number];
      bundleVersion?: number;
      bundleHash?: string;
    }>(`/api/models/${encodeURIComponent(id)}/modules/paste-section`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  replaceModuleInstance: (
    id: string,
    instanceId: string,
    payload: { catalogId: string; name?: string; createSnapshot?: boolean; expectedBundleVersion: number },
  ) =>
    request<ModuleInstanceLifecycleResult>(
      `/api/models/${encodeURIComponent(id)}/modules/instances/${encodeURIComponent(instanceId)}/replace`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ),
  bakeModuleEdits: (id: string, expectedBundleVersion: number, createSnapshot = true) =>
    request<ModuleInstanceLifecycleResult>(`/api/models/${encodeURIComponent(id)}/modules/bake-edits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ createSnapshot, expectedBundleVersion }),
    }),
  glbUrl: (id: string, artifactSha256?: string) =>
    `/api/models/${encodeURIComponent(id)}/glb${artifactSha256 ? `?v=${artifactSha256}` : ''}`,
  catalogModuleRelations: (catalogId: string) =>
    request<{
      schema: string;
      catalogId: string;
      name: string;
      meta_type: string;
      tier: string;
      dims: { w: number; h: number; d: number };
      family: { key: string; members: string[] };
      replaceCandidates: string[];
      materials: string[];
      siblingsByMaterial: Record<string, string[]>;
      constraints: { kind: string; target: string; label: string }[];
    }>(`/api/catalog/modules/${encodeURIComponent(catalogId)}/relations`),
  variationMatrix: (catalogId: string) =>
    request<VariationMatrixPayload>(`/api/catalog/modules/${encodeURIComponent(catalogId)}/variation-matrix`),
  historyFork: (modelId: string, payload: { versionId: string; branch: string; expectedVersion: number }) =>
    request<{
      ok: boolean;
      modelId: string;
      branch: string;
      cursor: number;
      versionId: string;
      forkedFrom: string;
      bundleVersion: number;
      bundleHash: string;
      replayed: boolean;
    }>(
      `/api/models/${encodeURIComponent(modelId)}/history/fork`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ),
  applyHardwarePreset: (
    id: string,
    payload: HardwareMaterialSelection & { group: string; useTipOn?: boolean; createSnapshot?: boolean; expectedBundleVersion: number },
  ) =>
    request<{
      ok: boolean;
      catalogId: number;
      group: string;
      counts: { instances: number };
      affectedEntityIds?: string[];
      bundleVersion: number;
    }>(`/api/models/${encodeURIComponent(id)}/hardware/apply-preset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  replaceHardwareInstance: (
    id: string,
    payload: HardwareMaterialSelection & {
      hardwareEntityId: string;
      group?: 'hinge' | 'slide' | 'handle' | string;
      createSnapshot?: boolean;
      expectedBundleVersion: number;
    },
  ) =>
    request<{
      ok: boolean;
      modelId: string;
      hardwareEntityId: string;
      catalog: Record<string, unknown>;
      operationIds: string[];
      model: ModelSummary;
      bundleVersion: number;
    }>(`/api/models/${encodeURIComponent(id)}/hardware/replace-instance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  moduleProjection: (modelId: string, moduleId: string) =>
    request<{
      schema: string;
      module: {
        module_id: string;
        title: string;
        module_kind: string;
        family_id: string;
        variant_id: string;
        domain: string;
        front_kind_v1: string;
        layout_v1: { schema: string; doors: number; drawers: number; sections: number };
        doors: number;
        drawers: number;
        sections: number;
        corpus_material_id: string;
        facade_material_id: string;
        edge_preset_id: string;
        edge_mode: string;
      };
      leaves: Array<{
        id: string;
        title: string;
        semantic_kind: string;
        part_type_ru: string;
        manufacturing_role: string;
        module_id: string;
        parent_id: string;
      }>;
    }>(`/api/models/${encodeURIComponent(modelId)}/projection/module/${encodeURIComponent(moduleId)}`),
  entityProjection: (modelId: string, entityId: string) =>
    request<{
      schema: string;
      entity: {
        id: string;
        title: string;
        semantic_kind: string;
        part_type_ru: string;
        manufacturing_role: string;
        module_id: string;
        parent_id: string;
      };
    }>(`/api/models/${encodeURIComponent(modelId)}/projection/entity/${encodeURIComponent(entityId)}`),
  bundleGraphs: (id: string) =>
    request<{
      schema: string;
      generatedAt: string;
      entitySignature: string;
      entityCount: number;
      moduleGraph: Record<string, unknown>;
      moduleEntities: Record<string, string[]>;
      entityModuleMap: Record<string, string>;
      indexes: {
        by_material: Record<string, string[]>;
        by_edge: Record<string, string[]>;
        by_role: Record<string, string[]>;
        by_kind: Record<string, string[]>;
      };
      rollups: Record<string, {
        module_id: string;
        corpus_m2: number;
        facade_m2: number;
        edge_m: number;
        hinges: number;
        slides: number;
        handles: number;
        screws: number;
        connectors: number;
        panels: number;
        facades: number;
        corpus_materials: string[];
        facade_materials: string[];
        edges: string[];
      }>;
      productionDag: Record<string, {
        module_id: string;
        panels: Array<{
          id: string;
          name: string;
          role: string;
          dims_mm: [number, number, number];
          material_id: string;
        }>;
        hardware: Array<{ id: string; kind: string; role: string; name: string }>;
        dependencies: Array<{ from: string; to: string; kind: string }>;
        stats: { panels: number; hardware: number };
      }>;
      spatialGrid: {
        cell_size_mm: number;
        cells: Record<string, string[]>;
        stats: { cell_count: number; max_per_cell: number };
      };
      constraintGraph?: {
        suggestions: Array<{ from: string; partnerPattern: string; kind: string; satisfiedBy: string | null }>;
        requires: Array<{ from: string; partnerPattern: string; kind: string; satisfiedBy: string | null }>;
        unsatisfied: Array<{ from: string; partnerPattern: string; kind: string; satisfiedBy: string | null }>;
        stats: { suggestions_count: number; requires_count: number; unsatisfied_count: number };
      };
      hardwareGraph?: HardwareGraphPayload;
    }>(`/api/models/${encodeURIComponent(id)}/graphs`),
  moduleGraph: (id: string) =>
    request<{
      schema: string;
      modules: Array<{
        id: string;
        tier: 'base' | 'wall' | 'tall' | 'unknown';
        catalogId: string;
        name: string;
        leaf_bbox: { min: [number, number, number]; max: [number, number, number]; size: [number, number, number] };
        entity_count: number;
        neighbours: { left: string | null; right: string | null; above: string | null; below: string | null };
      }>;
      byId: Record<string, number>;
      gaps_per_tier?: Record<
        string,
        Array<{
          kind: 'OVERLAP' | 'TOUCH' | 'SMALL' | 'FILLER' | 'MODULE';
          size_mm: number;
          left_module: string;
          right_module: string;
          x_start: number;
          x_end: number;
        }>
      >;
    }>(`/api/models/${encodeURIComponent(id)}/module-graph`),
  /* P2-аудит 10.10: тонкие обёртки для сырых fetch(`/api/...`) в кодовой базе.
   * Эти маршруты не имеют ниши в каталоге api.* (разовые операции, тонкие
   * пути — kitchen-run, electrical, facade-presets, plan-dimension-layers и т.п.);
   * добавлять по одной сущности под каждый = плодить сигнатуры, поэтому
   * единый get/post/patch/delete по идиоме существующих (таймаут, 401-сигнал,
   * JSON-парс — уже внутри request). JSON-body сериализуется здесь; для
   * blob/form-data остаются кастомные fetch — спецслучаи отдельно. */
  get: <T>(url: string, signal?: AbortSignal) => request<T>(url, { signal }),
  post: <T>(url: string, body: unknown, signal?: AbortSignal) =>
    request<T>(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    }),
  patch: <T>(url: string, body: unknown, signal?: AbortSignal) =>
    request<T>(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    }),
  delete: <T>(url: string, signal?: AbortSignal) => request<T>(url, { method: 'DELETE', signal }),
  put: <T>(url: string, body: unknown, signal?: AbortSignal, timeoutMs?: number) =>
    request<T>(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    }, timeoutMs),
};


type ModuleCombineResult = ModuleInstanceLifecycleResult & {
  instanceId?: string;
  sections?: number;
};

type ModuleSplitResult = ModuleInstanceLifecycleResult & {
  instances?: string[];
};
